import React, { useEffect, useRef, useState } from 'react';
import { Clock, Video } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
import { trackDemoBooked } from '../lib/analytics';
const CALENDLY_URL = 'https://calendly.com/tonyennis/ai-adoption-for-your-business-clone';
const CALENDLY_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';

// Calendly's own booking flow replaces the previous fake calendar — a mock
// scheduler on a real "Book a demo" page books nothing.
export function BookDemo() {
  useScreenInit();
  const holder = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    // Loaded at runtime rather than bundled so a blocked or slow script leaves
    // the page intact instead of failing the build.
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${CALENDLY_SCRIPT}"]`);
    const init = () => {
      const Calendly = (window as any).Calendly;
      if (!Calendly || !holder.current || holder.current.childElementCount > 0) return;
      Calendly.initInlineWidget({
        url: `${CALENDLY_URL}?hide_gdpr_banner=1&primary_color=ea580c`,
        parentElement: holder.current
      });
      setReady(true);
    };
    if ((window as any).Calendly) {
      init();
      return;
    }
    const script = existing ?? Object.assign(document.createElement('script'), {
      src: CALENDLY_SCRIPT,
      async: true
    });
    script.addEventListener('load', init);
    if (!existing) document.body.appendChild(script);
    return () => script.removeEventListener('load', init);
  }, []);
  // Calendly posts a message once a booking is confirmed inside the iframe —
  // the only way to know the demo was actually booked.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (typeof event.data?.event === 'string' && event.data.event === 'calendly.event_scheduled') {
        trackDemoBooked();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  return <div className="relative min-h-screen overflow-hidden bg-[#1C1510] px-6 py-24" data-id="element-6167" data-mp="3hrwahx6ozteq">
      <div className="dot-matrix-dark pointer-events-none absolute inset-0 opacity-50" data-id="element-6168" data-mp="3csjn9bbwskbm" />
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#F97316]/[0.08] blur-[120px]" data-id="element-6169" data-mp="3cue8yrv08ggr" />

      <div className="relative z-10 mx-auto w-full max-w-5xl" data-id="element-6170" data-mp="33ty9dthdtth6">
        <div className="text-center" data-id="element-6171" data-mp="2z0d6po4ugbbn">
          <h1 className="font-title text-4xl font-bold tracking-tight text-white md:text-5xl" data-id="element-6172" data-mp="2yzbuvhg07ksv">
            Book a Giga demo
          </h1>
          <p className="mx-auto mt-4 max-w-[52ch] text-lg leading-relaxed text-white/60" data-id="element-6173" data-mp="2p3qn12o97qb7">
            Thirty minutes, walked through your own tools — how Giga connects
            them, what it can run for you, and how access stays under control.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/50" data-id="element-6174" data-mp="2g4n3xyp37awp">
            <span className="inline-flex items-center gap-2" data-id="element-6175" data-mp="10jbggt2wlzvb">
              <Clock className="size-4" data-id="element-6176" data-mp="10hi5j1l9cthi" />
              30 minutes
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-6177" data-mp="14m7yahkmdry7">
              <Video className="size-4" data-id="element-6178" data-mp="19kjc13e1cmlp" />
              Google Meet link on confirmation
            </span>
          </div>
        </div>

        <div className="mt-10 overflow-hidden rounded-3xl border border-white/10 bg-white shadow-2xl" data-id="element-6179" data-mp="cv1zc4gmy0ai">
          {/* Calendly sizes its own iframe; the min-height keeps the card from
           collapsing while the widget loads. */}
          <div ref={holder} className="min-h-[760px] w-full" data-id="element-6180" data-mp="lslxv5n45ihf" />
          {!ready && <p className="px-6 pb-8 text-center text-sm text-neutral-500" data-id="element-6181" data-mp="qmqaa4f5eipw">
              Loading available times…{' '}
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#EA580C] underline" data-id="element-6182" data-mp="3p9b8b2pthccd">
                Open the scheduler directly
              </a>
            </p>}
        </div>
      </div>
    </div>;
}